import {Component, computed, inject, input, OnInit, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {filter} from 'rxjs';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {BreadcrumbDataService} from '../services/breadcrumb-data.service';
import {Breadcrumb} from '../models/interfaces/breadcrumb.interface';
import {ContentfulService} from '../services/contentful.service';
import {SearchEngineService} from '../services/search-engine.service';
import {ApiService} from 'src/app/core/http/api.service';
import {PortfolioSeriesDashboard} from '../models/classes/portfolio-series-dashboard.class';
import {LoggingService} from '../services/logging.service';
import {PortfolioSeriesMeta} from '../models/classes/portfolio-series-meta.class';
import {RelatedEntity} from '../models/classes/related-entity.class';
import {UserService} from '../services/user.service';
import {NgTourService} from '../services/ng-tour-ui.service';
import {NgTourStepsService} from '../services/ng-tour-steps.service';
import {EnvironmentVariablesService} from '../services/environment-variables.service';
import {MetadataService} from '../services/metadata.service';
import {ActivityTrackingService} from '../services/activity-tracking.service';
import {CommonModule} from '@angular/common';
import {NoAccessErrorComponent} from '../shared/no-access-error/no-access-error.component';
import {MatIconModule} from '@angular/material/icon';
import {AutoAdjustingDropdownComponent} from '../auto-adjusting-dropdown/auto-adjusting-dropdown.component';
import {FormsModule} from '@angular/forms';
import {DataUnavailableCardComponent} from '../shared/data-unavailable-card/data-unavailable-card.component';
import {DateFormatPipe} from '../pipes/date-format.pipe';
import {GcmDatePickerComponent, GcmLoadingService} from '@gcm/gcm-ui-angular';
import {TourMatMenu} from 'ngx-ui-tour-md-menu';
import {ErrorMessageService} from '../services/error-message.service';
import {
  SeriesDashboardPrivateEquityComponent
} from "./series-dashboard-private-equity/series-dashboard-private-equity.component";
import {
  SeriesDashboardRealEstateComponent
} from "./series-dashboard-real-estate/series-dashboard-real-estate.component";
import {
  SeriesDashboardInfrastructureComponent
} from "./series-dashboard-infrastructure/series-dashboard-infrastructure.component";
import {SeriesTableConfigService} from '../services/series-table-config.service';
import {AssetClassService} from '../services/asset-class.service';
import {AssetClass} from '../models/enums/asset-class.enum';

@Component({
  selector: 'app-series-dashboard',
  imports: [
    CommonModule,
    NoAccessErrorComponent,
    RouterModule,
    MatIconModule,
    AutoAdjustingDropdownComponent,
    FormsModule,
    DataUnavailableCardComponent,
    TourMatMenu,
    SeriesDashboardPrivateEquityComponent,
    SeriesDashboardRealEstateComponent,
    SeriesDashboardInfrastructureComponent,
    GcmDatePickerComponent
],
  templateUrl: './series-dashboard.component.html',
  styleUrls: ['./series-dashboard.component.scss']
})
export class SeriesDashboardComponent implements OnInit {
  private seoService = inject(SearchEngineService);
  private router = inject(Router);
  private contentfulService = inject(ContentfulService);
  private activatedRoute = inject(ActivatedRoute);
  breadcrumbService = inject(BreadcrumbDataService);
  private apiService = inject(ApiService);

  private loadingService = inject(GcmLoadingService);
  private loggingService = inject(LoggingService);
  private userService = inject(UserService);
  private ngTourSteps = inject(NgTourStepsService);
  private ngTourUi = inject(NgTourService);
  private evService = inject(EnvironmentVariablesService);
  private metadataService = inject(MetadataService);
  private activityTracking = inject(ActivityTrackingService);
  private dateFormat = inject(DateFormatPipe);
  private errorMessageService = inject(ErrorMessageService);
  private seriesTableConfig = inject(SeriesTableConfigService);
  private assetClassService = inject(AssetClassService);

  readonly investorId = input<number | undefined>(undefined);
  readonly seriesId = input<number | undefined>(undefined);

  readonly iconSet = toSignal(
    this.contentfulService.getIconSetValue().pipe(filter((v): v is Record<string, string> => !!v)),
    { initialValue: {} as Record<string, string> }
  );
  reportingPeriod = signal<string>('');

  readonly reportingdate = input<string | undefined>(undefined);

  private setReportingDate(value: string): void {
    if (!value) return;
    this.selectedAsOfDate.set(this.dateFormat.transform(value, "MM/dd/yyyy") ?? '');
    this.reportingPeriod.set(this.dateFormat.transform(value, "yyyy-MM-dd") ?? '');
  }

  portfolioDataForBreadcrumb: RelatedEntity[] = [];
  breadcrumbs: Breadcrumb[] = [];
  asOfDropdownData: string[] = [];
  selectedAsOfDate = signal<string>('');
  hasErrors = false;
  dashboardData = signal<PortfolioSeriesDashboard>(new PortfolioSeriesDashboard());
  seriesNameForBreadcrumb = signal<string>('');
  errorMessage = '';
  showNoAccessError = false;

  isPortfolioSeriesDataUnavailable = false;
  isAggregatedSeries = false;
  otherSectorNames: string[] = [];
  readonly AssetClass = AssetClass;
  portfolioSummary = computed(() => this.dashboardData()?.displayPortfolioDetail);

  assetClasses = computed(() => {
    const summary = this.dashboardData()?.summary;
    if (summary?.assetClass) return [summary.assetClass];

    const underlyingAssetClasses = [
      ...new Set(summary?.underlyingPortfolioSeries?.map((series) => series.assetClass).filter(Boolean)),
    ];
    return underlyingAssetClasses.length ? underlyingAssetClasses : undefined;
  });

  dashboardType = computed(() => {
    return this.assetClassService.getDashboardType(this.assetClasses() ?? []);
  });

  showAsPrivateEquity(): boolean {
    return this.dashboardType() === AssetClass.PrivateEquity;
  }

  constructor() {
    const ngTourSteps = this.ngTourSteps;

    this.ngTourSteps.getContentfulDataFetchedValue().subscribe((data: boolean) => {
      if (data) this.ngTourUi.setTourSteps(ngTourSteps.seriesDashboardSteps);
    })
  }

  ngOnInit(): void {
    this.showNoAccessError = false;
    this.seoService.setCanonicalURL();
    const date = this.reportingdate();
    if (date) this.setReportingDate(date);
    const investorId = this.investorId();
    this.getBreadcrumbDropdownData(investorId as number, this.seriesId() as number);
    if (this.userService.isExternalUser()) {
      this.checkExternalUserAccess(investorId as number)
    } else {
      this.getPortfolioSeriesDashboardDetails();
    }
  }

  checkExternalUserAccess(queryParamsinvestorid: string | number) {
    const assignedInvestorIds = this.userService.getInvestorIds();
    if (!assignedInvestorIds.includes(Number(queryParamsinvestorid))) {
      this.hasErrors = true;
      this.showNoAccessError = true;
      return;
    } else {
      this.hasErrors = false;
      this.getPortfolioSeriesDashboardDetails();
    }
  }

  getPortfolioSeriesDashboardDetails() {
    this.fetchDashboardDataFromApi(this.seriesId() as number, this.reportingPeriod()).then((data: PortfolioSeriesDashboard) => {
      this.assignDataToAllWidgets(data);
      if (!this.reportingPeriod()) {
        this.setReportingDate(data?.summary?.reportingPeriod?.slice(0, 10));
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: {
            reportingdate: this.reportingPeriod()
          },
          queryParamsHandling: 'merge'
        });
      }
    });
  }

  assignDataToAllWidgets(data: PortfolioSeriesDashboard) {
    this.dashboardData.set(data);
    const assetClasses = this.assetClasses();
    this.seriesTableConfig.setFromAssetClasses(assetClasses);

    this.isAggregatedSeries = data.summary?.underlyingPortfolioSeries?.length > 0;
    if (data.summary?.commitment) {
      this.isPortfolioSeriesDataUnavailable = false;
    } else {
      this.isPortfolioSeriesDataUnavailable = true;
      if (!this.asOfDropdownData.includes(this.selectedAsOfDate())) {
        this.asOfDropdownData.push(this.selectedAsOfDate());
      }
    }
    this.otherSectorNames = data.otherSectorNames && data.otherSectorNames.length > 0 ? data.otherSectorNames : [];
  }

  getBreadcrumbDropdownData(investorId: number, seriesId: number) {
    const loadingTask = 'getBreadcrumbDropdownData';
    this.loadingService.addAsyncTask(loadingTask);
    this.updateBreadcrumbs(investorId);
    // getting reporting periods and breadcrumb data from new API endpoint
    this.metadataService.fetchSeriesDashboardMetaData(seriesId, investorId)
      .then((data: PortfolioSeriesMeta) => {
        this.assignDataForBreadcrumbAndDropdown(data);
        this.loadingService.resolveAsyncTask(loadingTask);
      }).catch(err => {
        this.loggingService.log("----- Portfolio Series Dashboard Metadata API Exception -----", err);
        this.loadingService.resolveAsyncTask(loadingTask);
      });
  }

  updateBreadcrumbs(investorId: number) {
    this.breadcrumbs = this.breadcrumbService.getDashboardBreadcrumb(investorId);
    this.breadcrumbs.forEach(breadcrumb => {
      breadcrumb.queryParams = this.breadcrumbService.getQueryParamsForBreadcrumbs(breadcrumb);
    });
  }

  assignDataForBreadcrumbAndDropdown(data: PortfolioSeriesMeta) {
    this.asOfDropdownData = this.getAsOfDropdownData(data);
    if (!this.selectedAsOfDate()) {
      this.setReportingDate(this.asOfDropdownData[0]);
    } else if (this.isPortfolioSeriesDataUnavailable && !this.asOfDropdownData.includes(this.selectedAsOfDate())) {
      this.asOfDropdownData.push(this.selectedAsOfDate());
    }
    if (this.portfolioDataForBreadcrumb.length === 0) {
      this.portfolioDataForBreadcrumb = data.relatedEntities;
    }
    this.getSeriesName();
  }

  getAsOfDropdownData(data: PortfolioSeriesMeta): string[] {
    const cutoffTime = new Date(this.evService.hideDatesPriorToDate);
    return data.reportingPeriods
      .filter(date => new Date(date).getTime() >= cutoffTime.getTime())
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .map(date => this.dateFormat.transform(date, "MM/dd/yyyy") ?? '');
  }

  fetchDashboardDataFromApi(seriesId: number, reportingDate: string): Promise<PortfolioSeriesDashboard> {
    const loadingTask = 'fetchDashboardDataFromApi';
    this.loadingService.addAsyncTask(loadingTask);
    return new Promise((resolve, reject) => {
      this.apiService.getPortfolioSeriesDashboardDetails(this.investorId() as number, seriesId, reportingDate).subscribe({
        next: (data: PortfolioSeriesDashboard) => {
          this.loadingService.resolveAsyncTask(loadingTask);
          resolve(data);
        },
        error: (err) => {
          this.loadingService.resolveAsyncTask(loadingTask);
          this.hasErrors = true;
          this.errorMessage = this.errorMessageService.getErrorMessage(err);
          reject(err);
        }
      });
    })
  }

  updateAsOfDate(date: string) {
    this.activityTracking.toggledAsOfDate(date);
    //assigns selectedAsOfDate (MM/dd/yyyy) and reportingPeriod (yyyy-MM-dd)
    this.setReportingDate(date);

    this.fetchDashboardDataFromApi(this.seriesId() as number, this.reportingPeriod()).then((data: PortfolioSeriesDashboard) => {
      if (this.isPortfolioSeriesDataUnavailable) {
        this.metadataService.fetchSeriesDashboardMetaData(this.seriesId() as number, this.investorId() as number)
          .then((data: PortfolioSeriesMeta) => {
            this.asOfDropdownData = this.getAsOfDropdownData(data);
          }).catch(err => {
            this.loggingService.log("----- Portfolio Series Dashboard Metadata API Exception -----", err);
          });
      }
      this.assignDataToAllWidgets(data);
      this.router.navigate(
        [], {
        relativeTo: this.activatedRoute,
        queryParams: { reportingdate: this.reportingPeriod() },
        queryParamsHandling: 'merge'
      }).then(() => {
        this.updateBreadcrumbs(this.investorId() as number);
      });
    });
  }

  goToSelectedSeries(seriesId: number) {
    // getting reporting periods and breadcrumb data from new API endpoint
    this.metadataService.fetchSeriesDashboardMetaData(seriesId, this.investorId() as number)
      .then((metadata: PortfolioSeriesMeta) => {
        this.fetchDashboardDataFromApi(seriesId, this.selectedAsOfDate()).then((data: PortfolioSeriesDashboard) => {
          this.assignDataToAllWidgets(data);
          const route = `/investor/${this.investorId()}/series/${seriesId}`;
          this.router.navigate([route], {
            relativeTo: this.activatedRoute,
            queryParams: {
              reportingdate: this.reportingPeriod()
            }
          }).then(() => {
            this.assignDataForBreadcrumbAndDropdown(metadata);
          });
        });
      }).catch(err => {
        this.loggingService.log("----- Portfolio Series Dashboard Metadata API Exception -----", err);
      });
  }

  getSeriesName() {
    this.portfolioDataForBreadcrumb.map(portfolio => {
      const selectedSeries = portfolio.subEntities?.filter(portfolioSeries => portfolioSeries.displayId == Number(this.seriesId()))[0];
      if (selectedSeries) {
        this.seriesNameForBreadcrumb.set(selectedSeries.displayName);
      }
    });
  }

  breadcrumbClick(breadcrumb: Breadcrumb) {
    this.activityTracking.navigateToDashboard('Investor', breadcrumb, 'series dashboard breadcrumbs');
  }
}
